import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { selfApi } from '../../services/selfApi.js';
import { formatDate } from '../../utils/formatters.js';

/**
 * Zu meiner Entscheidung.
 *
 * Die Seite, die es bisher nicht gab. Die Regeln dafür, wer über einen
 * Ferienantrag entscheiden darf, standen schon im Dienst - nur konnte
 * niemand sie erreichen: /hr/leave-requests fällt unter das Modul 'hr', und
 * ein Vorgesetzter hat typischerweise die Rolle 'employee'. Er wurde vom
 * Gateway abgewiesen, bevor die sorgfältige Zuständigkeitsprüfung überhaupt
 * lief.
 *
 * Hier liegt beides beisammen, was eine vorgesetzte Person entscheidet:
 * Ferienanträge und Monatsabschlüsse. Getrennte Seiten hiessen, zweimal
 * nachsehen zu müssen, ob etwas liegen geblieben ist.
 */

const MONAT_VARIANTE = { SUBMITTED: 'info', APPROVED: 'success', REJECTED: 'danger', DRAFT: 'neutral' };

function signed(value) {
  const n = Number(value ?? 0);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}`;
}

/**
 * Eine Liste bleibt eine Liste.
 *
 * apiGet verpackt JEDE Antwort in { items, meta }. Ist items null, gibt
 * `data?.items ?? data` den UMSCHLAG zurück - und die Tabelle bekommt ein
 * Objekt statt einer Reihe. Der Fehler heisst dann "rows.map is not a
 * function" und zeigt auf die Tabelle, nicht auf die Stelle, an der er
 * entstand.
 */
function alsListe(wert) {
  const roh = Array.isArray(wert) ? wert : wert?.items;
  return Array.isArray(roh) ? roh : [];
}

export function MyTeamPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [fehler, setFehler] = useState(null);
  /* Der Antrag, dessen Überschneidungen gerade offen sind. Sie werden erst
     beim Öffnen geholt: die Frage "wer ist sonst noch weg" stellt sich pro
     Antrag, und sie für alle im Voraus zu laden hiesse, für jeden Eintrag
     eine Abfrage zu schicken, die meist niemand liest. */
  const [offenerAntrag, setOffenerAntrag] = useState(null);
  const [monatsDetail, setMonatsDetail] = useState(null);

  const profile = useQuery({ queryKey: ['self', 'profile'], queryFn: () => selfApi.profile() });
  const team = useQuery({
    queryKey: ['self', 'team'],
    queryFn: () => selfApi.team(),
    enabled: Boolean(profile.data),
  });
  const anträge = useQuery({
    queryKey: ['self', 'team', 'leave'],
    queryFn: () => selfApi.teamRequests({ status: 'PENDING' }),
    enabled: Boolean(profile.data),
  });
  const monate = useQuery({
    queryKey: ['self', 'team', 'months'],
    queryFn: () => selfApi.teamMonths({ status: 'SUBMITTED' }),
    enabled: Boolean(profile.data),
  });
  /* Die Stundenkonten. Sie stehen hier, weil eine Entscheidung über einen
     Kompensationstag ohne sie ein Rateschritt wäre - und ein Konto, das man
     erst nach der Freigabe sieht, hilft bei der Freigabe nicht. */
  const konten = useQuery({
    queryKey: ['self', 'team', 'timeAccounts'],
    queryFn: () => selfApi.teamTimeAccounts(),
    enabled: Boolean(profile.data),
  });

  const überschneidungen = useQuery({
    queryKey: ['self', 'team', 'overlaps', offenerAntrag?.id],
    queryFn: () => selfApi.teamOverlaps(offenerAntrag.id),
    enabled: Boolean(offenerAntrag),
  });
  const tage = useQuery({
    queryKey: ['self', 'team', 'monthEntries', monatsDetail?.employeeId, monatsDetail?.year, monatsDetail?.month],
    queryFn: () =>
      selfApi.teamMonthEntries(monatsDetail.employeeId, {
        year: monatsDetail.year,
        month: monatsDetail.month,
      }),
    enabled: Boolean(monatsDetail),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['self', 'team'] });
  };

  const entscheideAntrag = useMutation({
    mutationFn: ({ id, decision }) => selfApi.decideRequest(id, { decision }),
    onSuccess: () => {
      setFehler(null);
      setOffenerAntrag(null);
      refresh();
    },
    onError: (err) => setFehler(err?.message || t('common.loadError')),
  });

  const entscheideMonat = useMutation({
    mutationFn: ({ employeeId, year, month, decision, decisionNote }) =>
      selfApi.decideMonth(employeeId, { decision, year, month, decisionNote: decisionNote || null }),
    onSuccess: () => {
      setFehler(null);
      setMonatsDetail(null);
      refresh();
    },
    onError: (err) => setFehler(err?.message || t('common.loadError')),
  });

  if (profile.error) {
    const code = profile.error?.code;
    return (
      <div className="alert warning">
        {code === 'NO_EMPLOYEE_LINK' ? t('self.noLink') : profile.error.message}
      </div>
    );
  }

  const meine = alsListe(team.data);
  const offeneAnträge = alsListe(anträge.data);
  const offeneMonate = alsListe(monate.data);
  const stundenkonten = alsListe(konten.data);
  const kontoVon = (employeeId) => stundenkonten.find((k) => k.employeeId === employeeId) || null;

  /* Wer niemanden führt, soll keine leere Verwaltungsseite sehen, sondern
     einen Satz, der erklärt warum. */
  if (!team.isLoading && meine.length === 0) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1>{t('team.title')}</h1>
            <p className="muted">{t('team.subtitle')}</p>
          </div>
        </div>
        <div className="decide-empty">{t('team.noReports')}</div>
      </>
    );
  }

  const antragSpalten = [
    { key: 'who', label: t('team.col.who'), render: (r) => r.employeeName },
    {
      key: 'range',
      label: t('team.col.range'),
      render: (r) => `${formatDate(r.startDate)} - ${formatDate(r.endDate)}`,
    },
    { key: 'days', label: t('team.col.days'), align: 'right', render: (r) => <span className="num mono">{Number(r.days).toFixed(1)}</span> },
    { key: 'reason', label: t('team.col.reason'), render: (r) => r.reason || '-' },
    {
      key: 'do',
      label: '',
      align: 'right',
      render: (r) => (
        <div className="decide-actions">
          <Button size="sm" variant="ghost" onClick={() => setOffenerAntrag(r)}>
            {t('team.check')}
          </Button>
        </div>
      ),
    },
  ];

  const monatsSpalten = [
    { key: 'who', label: t('team.col.who'), render: (r) => r.employeeName },
    {
      key: 'month',
      label: t('team.col.month'),
      render: (r) => `${t(`month.name.${r.month}`)} ${r.year}`,
    },
    {
      key: 'hours',
      label: t('team.col.hours'),
      align: 'right',
      render: (r) => (
        <span className="num mono">{Number(r.submittedActualHours ?? 0).toFixed(2)}</span>
      ),
    },
    {
      key: 'status',
      label: t('team.col.status'),
      render: (r) => <Badge variant={MONAT_VARIANTE[r.status]}>{t(`month.status.${r.status}`)}</Badge>,
    },
    {
      key: 'do',
      label: '',
      align: 'right',
      render: (r) => (
        <div className="decide-actions">
          <Button size="sm" variant="ghost" onClick={() => setMonatsDetail(r)}>
            {t('team.check')}
          </Button>
        </div>
      ),
    },
  ];

  /* Woraus sich ein Konto zusammensetzt, in einer Zeile. Ohne diese Angabe
     wäre die grosse Zahl eine Behauptung - und über eine Behauptung
     entscheidet niemand gern. */
  const kontoSpalten = [
    { key: 'who', label: t('team.col.who'), render: (r) => r.employeeName },
    {
      key: 'total',
      label: t('timeAccount.col.total'),
      align: 'right',
      render: (r) => (
        <span
          className="num mono"
          data-sign={Number(r.totalHours) > 0 ? 'plus' : Number(r.totalHours) < 0 ? 'minus' : undefined}
        >
          {r.totalHours === null || r.totalHours === undefined ? '-' : signed(r.totalHours)}
        </span>
      ),
    },
    {
      key: 'opening',
      label: t('timeAccount.col.opening'),
      align: 'right',
      render: (r) => <span className="num mono">{signed(r.openingHours)}</span>,
    },
    {
      key: 'closed',
      label: t('timeAccount.col.closed'),
      align: 'right',
      render: (r) => <span className="num mono">{signed(r.closedHours)}</span>,
    },
    {
      key: 'open',
      label: t('timeAccount.col.open'),
      align: 'right',
      render: (r) => <span className="num mono">{signed(r.openHours)}</span>,
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('team.title')}</h1>
          <p className="muted">{t('team.subtitle')}</p>
        </div>
      </div>

      {fehler && <div className="alert danger">{fehler}</div>}

      <section className="decide-section">
        <h2>{t('team.leave.title')}</h2>
        <p className="muted text-sm">{t('team.leave.hint')}</p>
        <Table
          columns={antragSpalten}
          rows={offeneAnträge}
          isLoading={anträge.isLoading}
          emptyMessage={t('team.leave.empty')}
        />
      </section>

      <section className="decide-section">
        <h2>{t('team.months.title')}</h2>
        <p className="muted text-sm">{t('team.months.hint')}</p>
        <Table
          columns={monatsSpalten}
          rows={offeneMonate}
          isLoading={monate.isLoading}
          emptyMessage={t('team.months.empty')}
          getRowKey={(r) => r.id}
        />
      </section>

      <section className="decide-section">
        <h2>{t('timeAccount.team.title')}</h2>
        <p className="muted text-sm">{t('timeAccount.team.hint')}</p>
        <Table
          columns={kontoSpalten}
          rows={stundenkonten}
          isLoading={konten.isLoading}
          emptyMessage={t('timeAccount.team.empty')}
          getRowKey={(r) => r.employeeId}
        />
      </section>

      {/* Ferienantrag entscheiden - mit der Angabe, wer sonst noch weg ist.
          Ohne sie entscheidet man blind und merkt erst hinterher, dass die
          halbe Abteilung in derselben Woche fehlt. */}
      <Modal
        open={Boolean(offenerAntrag)}
        title={t('team.leave.decide')}
        onClose={() => setOffenerAntrag(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOffenerAntrag(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => entscheideAntrag.mutate({ id: offenerAntrag.id, decision: 'REJECT' })}
              disabled={entscheideAntrag.isPending}
            >
              {t('team.reject')}
            </Button>
            <Button
              variant="primary"
              onClick={() => entscheideAntrag.mutate({ id: offenerAntrag.id, decision: 'APPROVE' })}
              disabled={entscheideAntrag.isPending}
            >
              {t('team.approve')}
            </Button>
          </>
        }
      >
        {offenerAntrag && (
          <>
            <p>
              <strong>{offenerAntrag.employeeName}</strong>
              {' - '}
              {formatDate(offenerAntrag.startDate)} bis {formatDate(offenerAntrag.endDate)}
              {' ('}
              {Number(offenerAntrag.days).toFixed(1)} {t('team.daysUnit')}
              {')'}
            </p>
            {offenerAntrag.reason && <p className="muted">{offenerAntrag.reason}</p>}

            {/* Der Kontostand des Antragstellers, an genau der Stelle, an
                der er gebraucht wird. Ein Kompensationstag zehrt das Konto
                auf; ohne diese Zeile entscheidet man, ohne zu wissen, ob er
                gedeckt ist. */}
            {kontoVon(offenerAntrag.employeeId) && (
              <p className="text-sm">
                {t('timeAccount.forDecision', {
                  hours: signed(kontoVon(offenerAntrag.employeeId).totalHours),
                })}
              </p>
            )}

            <h3 className="text-sm">{t('team.overlaps.title')}</h3>
            {überschneidungen.isLoading ? (
              <p className="muted text-sm">{t('self.loading')}</p>
            ) : alsListe(überschneidungen.data).length === 0 ? (
              <p className="muted text-sm">{t('team.overlaps.none')}</p>
            ) : (
              <ul>
                {alsListe(überschneidungen.data).map((o) => (
                  <li key={o.id}>
                    {o.employeeName}: {formatDate(o.startDate)} - {formatDate(o.endDate)}{' '}
                    <Badge variant={o.status === 'APPROVED' ? 'success' : 'warning'}>
                      {t(`status.${o.status}`)}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Modal>

      {/* Monat entscheiden - mit jedem Tag davor. Eine Freigabe ohne Blick
          auf die Zeilen wäre ein Blindflug. */}
      <Modal
        open={Boolean(monatsDetail)}
        title={t('team.months.decide')}
        onClose={() => setMonatsDetail(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setMonatsDetail(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                entscheideMonat.mutate({
                  employeeId: monatsDetail.employeeId,
                  year: monatsDetail.year,
                  month: monatsDetail.month,
                  decision: 'REJECT',
                  decisionNote: monatsDetail.note,
                })
              }
              disabled={entscheideMonat.isPending}
            >
              {t('team.reject')}
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                entscheideMonat.mutate({
                  employeeId: monatsDetail.employeeId,
                  year: monatsDetail.year,
                  month: monatsDetail.month,
                  decision: 'APPROVE',
                })
              }
              disabled={entscheideMonat.isPending}
            >
              {t('team.approveMonth')}
            </Button>
          </>
        }
      >
        {monatsDetail && (
          <>
            <p>
              <strong>{monatsDetail.employeeName}</strong>
              {' - '}
              {t(`month.name.${monatsDetail.month}`)} {monatsDetail.year}
            </p>
            {tage.isLoading ? (
              <p className="muted text-sm">{t('self.loading')}</p>
            ) : (
              <>
                <dl className="month-bar-figures">
                  <div>
                    <dt>{t('month.target')}</dt>
                    <dd className="num mono">{Number(tage.data?.targetHours ?? 0).toFixed(2)}</dd>
                  </div>
                  <div>
                    <dt>{t('month.actual')}</dt>
                    <dd className="num mono">{Number(tage.data?.actualHours ?? 0).toFixed(2)}</dd>
                  </div>
                  <div>
                    <dt>{t('month.balance')}</dt>
                    <dd className="num mono">{signed(tage.data?.balanceHours)}</dd>
                  </div>
                </dl>
                {/* Nur die Tage, an denen etwas fehlt oder steht. Alle 31
                    aufzuzählen macht die Liste unlesbar und die Lücken
                    unsichtbar. */}
                <ul className="text-sm">
                  {(tage.data?.days ?? [])
                    .filter((d) => d.missing || d.actualHours > 0)
                    .map((d) => (
                      <li key={d.date}>
                        <span className="mono">{d.date.slice(8)}.{d.date.slice(5, 7)}.</span>{' '}
                        {d.missing ? (
                          <Badge variant="warning">{t('time.week.missing')}</Badge>
                        ) : (
                          <span className="num mono">{Number(d.actualHours).toFixed(2)}</span>
                        )}
                      </li>
                    ))}
                </ul>
              </>
            )}

            <label className="field">
              <span className="field-label">{t('team.months.note')}</span>
              <input
                className="input"
                value={monatsDetail.note || ''}
                onChange={(e) => setMonatsDetail((p) => ({ ...p, note: e.target.value }))}
                placeholder={t('team.months.notePlaceholder')}
              />
            </label>
          </>
        )}
      </Modal>
    </>
  );
}
